import * as React from "react"
import { createPortal } from "react-dom"
import { useLocation, useNavigate } from "react-router-dom"
import { ArrowLeft, ArrowRight, Compass, X } from "lucide-react"

import { Button } from "@/components/ui/button"
import { t } from "@/lib/i18n"
import { cn } from "@/lib/utils"

import { CAPITULOS, PASOS } from "@/components/guia/pasos"
import type { Paso } from "@/components/guia/pasos"

/**
 * LA GUÍA: un recorrido por toda la herramienta, elemento por elemento.
 *
 * Cesar, punto 4 de la cuarta tanda, y después, más concreto (2026-10-01):
 * «necesito una guía como Deel lo hace, en donde te lleva pantalla por pantalla
 * comentándote qué encuentras en cada cosa y con botón de siguiente».
 *
 * ===========================================================================
 * QUÉ HACE CADA PASO
 * ===========================================================================
 *
 *   1. Lleva a la pantalla del paso (navega de verdad).
 *   2. Busca el elemento y lo trae a la vista.
 *   3. Oscurece todo lo demás y deja el elemento iluminado —el «foco»—, con el
 *      globo al lado diciendo qué es y qué se puede hacer ahí.
 *
 * El guion vive en `guia/pasos.ts`; esto es sólo el motor.
 *
 * ---------------------------------------------------------------------------
 * MIENTRAS DURA, LA PANTALLA NO SE TOCA
 *
 * Una capa transparente encima recibe los clics. Si se pudiera pulsar debajo,
 * un clic suelto navegaría a otra pantalla y el globo seguiría hablando de la
 * anterior. Se sale con «Saltar», con la X o con Esc.
 *
 * ---------------------------------------------------------------------------
 * UN ELEMENTO QUE NO ESTÁ NO ROMPE LA GUÍA
 *
 * Una tabla vacía no tiene panel, un rol no ve Holding. Si el objetivo no
 * aparece en ~2,5 s, el globo sale centrado y explica igual. Si el paso
 * `requiere` algo que la página no tiene (la entrada del menú de una sección
 * que el rol no ve), se salta.
 *
 * ---------------------------------------------------------------------------
 * SE RECUERDA QUE SE VIO, Y NO SE VUELVE A ABRIR SOLA
 *
 * En `localStorage` y por cliente. Se abre desde el menú del avatar, con
 * `?guia=1` en cualquier ruta, o al terminar Primeros pasos.
 */

const CLAVE = "marty.guia.vista"

function yaLaVio(tenantId: string | null): boolean {
  try {
    return localStorage.getItem(`${CLAVE}.${tenantId ?? "sin"}`) === "1"
  } catch {
    return false
  }
}

export function marcarGuiaVista(tenantId: string | null): void {
  try {
    localStorage.setItem(`${CLAVE}.${tenantId ?? "sin"}`, "1")
  } catch {
    /* sin localStorage: no pasa nada, se volvería a ofrecer */
  }
}

export function olvidarGuiaVista(tenantId: string | null): void {
  try {
    localStorage.removeItem(`${CLAVE}.${tenantId ?? "sin"}`)
  } catch {
    /* ver arriba */
  }
}

export function guiaPendiente(tenantId: string | null): boolean {
  return !yaLaVio(tenantId)
}

/** Holgura del foco alrededor del elemento, en px. */
const MARGEN = 6
/** Ancho del globo. */
const ANCHO = 380
/** Cuánto se espera a que aparezca el elemento antes de rendirse. */
const ESPERA_MS = 2500

function visible(el: Element): boolean {
  const r = el.getBoundingClientRect()
  return r.width > 0 && r.height > 0
}

/** El elemento del paso, o `null`. */
function buscar(paso: Paso): HTMLElement | null {
  if (!paso.objetivo) return null
  const candidatos = Array.from(document.querySelectorAll<HTMLElement>(paso.objetivo))
    .filter(visible)
  if (!paso.texto) return candidatos[0] ?? null
  // SE COMPARA TRADUCIDO Y SIN TRADUCIR: la interfaz puede estar en portugués,
  // y no todos los rótulos pasan por el diccionario (el título del estado de
  // resultados se compone con el año). Medido en portugués: sin la segunda
  // comparación, tres pasos salían sin foco.
  const frases = [t(paso.texto), paso.texto].map((f) => f.toLowerCase())
  const encontrado = candidatos.find((el) => {
    const texto = (el.textContent ?? "").toLowerCase()
    return frases.some((f) => texto.includes(f))
  })
  if (encontrado) return encontrado
  // Si ninguna aparece, la fila de pestañas de MÁS ADENTRO —la de la sección
  // en la que se está— y no la de fuera, que es la del módulo.
  return (paso.objetivo.includes("pestanas") ? candidatos[candidatos.length - 1] : candidatos[0]) ?? null
}

interface Rect { top: number; left: number; width: number; height: number }

/** Más alto que esto (fracción de la ventana) es un elemento «alto». */
const ALTO = 0.55

function esAlto(el: HTMLElement): boolean {
  return el.getBoundingClientRect().height > window.innerHeight * ALTO
}

function rectDe(el: HTMLElement): Rect {
  const r = el.getBoundingClientRect()
  const top = Math.max(r.top, 8)
  let bottom = Math.min(r.bottom, window.innerHeight - 8)
  // UN ELEMENTO ALTO SE ILUMINA POR ARRIBA. El estado de resultados mide dos
  // pantallas: recortado a la ventana, el foco era la ventana entera y no
  // señalaba nada. Se queda con su cabecera y las primeras filas, que es lo que
  // el globo describe, y deja sitio debajo para el globo.
  if (r.height > window.innerHeight * ALTO) {
    bottom = Math.min(bottom, top + window.innerHeight * 0.4)
  }
  return { top, left: r.left, width: r.width, height: Math.max(bottom - top, 24) }
}

/** Dónde va el globo: debajo si cabe, si no encima, si no al lado; siempre dentro. */
function posicionDelGlobo(rect: Rect | null, alto: number): React.CSSProperties {
  const vw = window.innerWidth
  const vh = window.innerHeight
  const ancho = Math.min(ANCHO, vw - 24)
  if (!rect) {
    return { top: Math.max((vh - alto) / 2, 12), left: (vw - ancho) / 2, width: ancho }
  }
  const sujetar = (x: number, min: number, max: number) => Math.min(Math.max(x, min), max)
  const izquierdaCentrada = sujetar(rect.left + rect.width / 2 - ancho / 2, 12, vw - ancho - 12)
  const abajo = rect.top + rect.height + MARGEN + 12
  if (abajo + alto < vh - 12) return { top: abajo, left: izquierdaCentrada, width: ancho }
  const arriba = rect.top - MARGEN - 12 - alto
  if (arriba > 12) return { top: arriba, left: izquierdaCentrada, width: ancho }
  // Ni arriba ni abajo: al lado con más espacio, centrado en vertical.
  const top = sujetar(rect.top + rect.height / 2 - alto / 2, 12, vh - alto - 12)
  const derecha = rect.left + rect.width + MARGEN + 12
  if (derecha + ancho < vw - 12) return { top, left: derecha, width: ancho }
  return { top, left: sujetar(rect.left - MARGEN - 12 - ancho, 12, vw - ancho - 12), width: ancho }
}

export function GuiaDeLaPlataforma({
  abierta, onCerrar, tenantId,
}: {
  abierta: boolean
  onCerrar: () => void
  tenantId: string | null
}) {
  const navegar = useNavigate()
  const { pathname } = useLocation()
  // El paso también sobrevive al remontaje por cambio de idioma (ver AppLayout).
  const [i, setI] = React.useState(() => {
    try { return Number(sessionStorage.getItem("marty.guia.paso") ?? 0) || 0 } catch { return 0 }
  })
  React.useEffect(() => {
    try {
      if (abierta) sessionStorage.setItem("marty.guia.paso", String(i))
      else sessionStorage.removeItem("marty.guia.paso")
    } catch { /* ver arriba */ }
  }, [abierta, i])
  /** +1 avanzando, −1 retrocediendo: hacia dónde se salta un paso que no aplica. */
  const sentido = React.useRef(1)
  const [rect, setRect] = React.useState<Rect | null>(null)
  const [buscando, setBuscando] = React.useState(true)
  const globo = React.useRef<HTMLDivElement>(null)
  const [altoGlobo, setAltoGlobo] = React.useState(220)
  const objetivo = React.useRef<HTMLElement | null>(null)

  const paso = PASOS[Math.min(i, PASOS.length - 1)]
  const total = PASOS.length

  // AL ABRIR, se empieza por el principio. Pero sólo al ABRIR (cerrada → abierta
  // en esta vida del componente): si se montó ya abierta es que se recuperó de
  // un cambio de idioma, y volver al paso 1 sería perder el recorrido.
  const estabaAbierta = React.useRef(abierta)
  React.useEffect(() => {
    if (abierta && !estabaAbierta.current) { setI(0); sentido.current = 1 }
    estabaAbierta.current = abierta
  }, [abierta])

  // POR REF: el layout pasa `onCerrar` como función nueva en cada render, y
  // como dependencia reiniciaría la búsqueda del elemento en cada navegación.
  const alCerrar = React.useRef(onCerrar)
  alCerrar.current = onCerrar
  const cerrar = React.useCallback(() => {
    marcarGuiaVista(tenantId)
    alCerrar.current()
  }, [tenantId])

  const ir = React.useCallback((n: number) => {
    sentido.current = n >= i ? 1 : -1
    if (n < 0) return
    if (n >= total) { cerrar(); return }
    setI(n)
  }, [i, total, cerrar])

  // 1 · LA PANTALLA DEL PASO
  React.useEffect(() => {
    if (!abierta || !paso) return
    if (pathname !== paso.ruta) navegar(paso.ruta)
    // `pathname` NO es dependencia: navegar a mano no tiene que devolverte.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abierta, i])

  // 2 · EL ELEMENTO: se espera a que aparezca, se trae a la vista y se mide
  React.useEffect(() => {
    if (!abierta || !paso) return
    let vivo = true
    setBuscando(true)
    setRect(null)
    objetivo.current = null
    const inicio = Date.now()

    const intentar = () => {
      if (!vivo) return
      // Un paso sin elemento (la bienvenida) sale ya: esperar a «no encontrar»
      // nada dejaba la pantalla oscura y vacía dos segundos y medio.
      if (!paso.objetivo && !paso.requiere) {
        setBuscando(false)
        return
      }
      // Un paso de una sección que este rol no ve: se salta en la dirección en
      // que se iba. El menú ya está montado, así que no hace falta esperar.
      if (paso.requiere && !document.querySelector(paso.requiere)) {
        const siguiente = i + sentido.current
        if (siguiente >= 0 && siguiente < total) setI(siguiente)
        else cerrar()
        return
      }
      const el = window.location.pathname === paso.ruta ? buscar(paso) : null
      if (el) {
        objetivo.current = el
        // Uno alto se trae por ARRIBA; centrado, se vería su mitad.
        el.scrollIntoView({ block: esAlto(el) ? "start" : "center", inline: "nearest",
                            behavior: "smooth" })
        // Se mide cuando el desplazamiento termina; antes, el foco saldría
        // donde estaba el elemento y no donde queda.
        window.setTimeout(() => {
          if (!vivo) return
          setRect(rectDe(el))
          setBuscando(false)
        }, 380)
        return
      }
      if (Date.now() - inicio > ESPERA_MS) {
        setBuscando(false)   // sin objetivo: globo centrado
        return
      }
      window.setTimeout(intentar, 150)
    }
    // Una pausa para que la pantalla nueva empiece a montarse.
    const arranque = window.setTimeout(intentar, 250)
    return () => { vivo = false; window.clearTimeout(arranque) }
  }, [abierta, i, paso, total, cerrar])

  // El foco sigue al elemento si la ventana cambia o algo se mueve debajo.
  React.useEffect(() => {
    if (!abierta) return
    const medir = () => {
      if (objetivo.current && document.body.contains(objetivo.current)) {
        setRect(rectDe(objetivo.current))
      }
    }
    window.addEventListener("resize", medir)
    window.addEventListener("scroll", medir, true)
    const intervalo = window.setInterval(medir, 600)
    return () => {
      window.removeEventListener("resize", medir)
      window.removeEventListener("scroll", medir, true)
      window.clearInterval(intervalo)
    }
  }, [abierta])

  React.useLayoutEffect(() => {
    if (globo.current) setAltoGlobo(globo.current.offsetHeight)
  })

  // Teclado: → / Enter avanza, ← retrocede, Esc sale.
  React.useEffect(() => {
    if (!abierta) return
    const tecla = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight" || e.key === "Enter") { e.preventDefault(); ir(i + 1) }
      else if (e.key === "ArrowLeft") { e.preventDefault(); ir(i - 1) }
      else if (e.key === "Escape") { e.preventDefault(); cerrar() }
    }
    window.addEventListener("keydown", tecla)
    return () => window.removeEventListener("keydown", tecla)
  }, [abierta, i, ir, cerrar])

  if (!abierta || !paso) return null

  const capitulo = paso.capitulo
  const primeroDelCapitulo = (c: string) => PASOS.findIndex((p) => p.capitulo === c)
  const ultimo = i === total - 1
  const foco = !buscando && rect

  return createPortal(
    <div className="fixed inset-0 z-[70]" aria-live="polite">
      {/* La capa que bloquea los clics de debajo. */}
      <div className="absolute inset-0" onClick={(e) => e.stopPropagation()} />

      {foco ? (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute rounded-lg ring-2 ring-brand transition-all duration-300 ease-out"
          style={{
            top: rect.top - MARGEN, left: rect.left - MARGEN,
            width: rect.width + MARGEN * 2, height: rect.height + MARGEN * 2,
            // El oscurecido ES la sombra del foco: un solo elemento, sin cuatro
            // rectángulos que se descuadren al animar.
            boxShadow: "0 0 0 9999px rgba(12, 22, 17, 0.58)",
          }}
        />
      ) : (
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-[rgba(12,22,17,0.58)] transition-opacity" />
      )}

      <div
        ref={globo}
        role="dialog"
        aria-modal="true"
        aria-label={t("Guía de la plataforma")}
        className={cn(
          "absolute rounded-panel border bg-surface p-3.5 shadow-xl",
          "transition-[top,left] duration-300 ease-out",
          buscando && "opacity-0",
        )}
        style={posicionDelGlobo(foco ? rect : null, altoGlobo)}
      >
        <div className="flex items-start gap-2">
          <Compass className="mt-0.5 h-4 w-4 shrink-0 text-brand" aria-hidden="true" />
          <div className="min-w-0 flex-1">
            <p className="eyebrow text-muted-foreground">
              {t(capitulo)} · {t("paso")} {i + 1} {t("de")} {total}
            </p>
            <h2 className="mt-0.5 text-sm font-medium leading-tight text-foreground">{t(paso.titulo)}</h2>
          </div>
          <Button size="iconSm" variant="ghost" aria-label={t("Cerrar la guía")} onClick={cerrar}>
            <X />
          </Button>
        </div>

        <div
          className="mt-2 h-1 overflow-hidden rounded-full bg-surface-sunken"
          role="progressbar" aria-valuemin={1} aria-valuemax={total} aria-valuenow={i + 1}
        >
          <div className="h-full bg-brand transition-all duration-300" style={{ width: `${((i + 1) / total) * 100}%` }} />
        </div>

        <p className="mt-2.5 text-xs leading-5 text-foreground">{t(paso.cuerpo)}</p>
        {paso.accion ? (
          <p className="mt-1.5 rounded-md bg-brand-soft px-2 py-1.5 text-2xs leading-5 text-foreground">
            {t(paso.accion)}
          </p>
        ) : null}

        <div className="mt-3 flex items-center gap-2">
          <select
            aria-label={t("Ir a un capítulo")}
            value={capitulo}
            onChange={(e) => ir(primeroDelCapitulo(e.target.value))}
            className="h-7 min-w-0 flex-1 rounded-md border bg-surface px-1.5 text-2xs text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {CAPITULOS.map((c) => <option key={c} value={c}>{t(c)}</option>)}
          </select>
          <Button size="xs" variant="ghost" disabled={i === 0} onClick={() => ir(i - 1)}>
            <ArrowLeft />
            {t("Anterior")}
          </Button>
          <Button size="xs" onClick={() => ir(i + 1)} autoFocus>
            {ultimo ? t("Terminar") : t("Siguiente")}
            {ultimo ? null : <ArrowRight />}
          </Button>
        </div>
        {ultimo ? null : (
          <button
            type="button"
            onClick={cerrar}
            className="mt-2 text-2xs text-muted-foreground underline-offset-2 hover:underline"
          >
            {t("Saltar la guía")}
          </button>
        )}
      </div>
    </div>,
    document.body,
  )
}
